/**
 * Tiny synthesised "dispatch beep" used to discreetly alert agency operators
 * when a new emergency alert lands on their dashboard. Pure Web Audio API,
 * no asset to load.
 *
 * Two short tones (high → low) ~ 220 ms total. Volume capped at 0.25 so it
 * never feels alarming. Browsers require a previous user gesture before
 * audio can play; the dashboard uses a one-time "Activer le son" button to
 * unlock it.
 */

let ctx = null;
let unlocked = false;

const ensureCtx = () => {
  if (ctx) return ctx;
  try {
    const Klass = window.AudioContext || window.webkitAudioContext;
    if (!Klass) return null;
    ctx = new Klass();
    return ctx;
  } catch (_e) {
    return null;
  }
};

const beep = (freq, startAt, durMs) => {
  if (!ctx) return;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.value = freq;
  gain.gain.setValueAtTime(0, ctx.currentTime + startAt);
  gain.gain.linearRampToValueAtTime(0.25, ctx.currentTime + startAt + 0.01);
  gain.gain.linearRampToValueAtTime(0, ctx.currentTime + startAt + durMs / 1000);
  osc.connect(gain).connect(ctx.destination);
  osc.start(ctx.currentTime + startAt);
  osc.stop(ctx.currentTime + startAt + durMs / 1000 + 0.02);
};

export const dispatchSound = {
  isUnlocked() { return unlocked; },
  /**
   * Must be called from a user gesture handler (click) to unlock audio
   * playback in browsers that suspend AudioContext until user interacts.
   */
  async unlock() {
    const c = ensureCtx();
    if (!c) return false;
    try {
      if (c.state === "suspended") await c.resume();
      unlocked = true;
      return true;
    } catch (_e) {
      return false;
    }
  },
  play() {
    const c = ensureCtx();
    if (!c || !unlocked) return;
    try {
      // Two-tone "dispatch": 880 Hz then 660 Hz, ~110 ms each
      beep(880, 0, 110);
      beep(660, 0.13, 130);
    } catch (e) { /* noop */ void e; }
  },
};
